import { supabase } from '@/lib/supabase';
import { toUserMessage, enrichConstraintMessage } from '@/lib/errors';
import { log } from '@/lib/logger';
import type { Tables, TablesInsert, TablesUpdate } from '@/types/database';
import { ok, err, type ApiResponse } from './_base';

export type Edificio = Tables<'edificios'>;
export type EdificioInsert = TablesInsert<'edificios'>;
export type EdificioUpdate = TablesUpdate<'edificios'>;

export interface EdificioRow extends Edificio {
  administracion_nombre?: string;
  administracion_codigo?: string;
}

export interface ListEdificiosFilters {
  empresaId: string;
  /** Un único id (shortcut) */
  administracionId?: string;
  /** Varios ids → filtra con `.in(...)` */
  administracionIds?: string[];
  search?: string;
  activo?: boolean | 'all';
  page?: number;
  pageSize?: number;
}

export interface PagedResult<T> {
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
}

/** Lista con filtros + paginación server-side. Incluye nombre/código de admin. */
export async function listEdificios(
  filters: ListEdificiosFilters,
): Promise<ApiResponse<PagedResult<EdificioRow>>> {
  const page = Math.max(1, filters.page ?? 1);
  // Cap de 2000: con más de ~2000 edificios activos la UX exige server-side
  // search; mientras tanto este cap permite que los combos (facturación,
  // conciliación, cobranza múltiple) carguen toda la cartera de una cliente
  // real sin cortar alfabéticamente (E37 — RAPIFAST en pos 924 quedaba fuera
  // con el cap anterior de 200, era invisible en el combo).
  const pageSize = Math.min(2000, Math.max(10, filters.pageSize ?? 50));
  const from = (page - 1) * pageSize;
  const to = from + pageSize - 1;

  let q = supabase
    .from('edificios')
    .select(
      'id, empresa_id, administracion_id, codigo, nombre, unidades, cocheras, bauleras, empleados, tipo_documento, numero_documento, condicion_iva, domicilio, provincia, localidad, codigo_postal, monto_abono, activo, facturar_con_cuit_administracion, observaciones, created_at, updated_at, nombre_normalizado, created_by, administraciones!inner(codigo, nombre)',
      { count: 'exact' },
    )
    .eq('empresa_id', filters.empresaId)
    .order('nombre_normalizado');

  if (filters.administracionIds && filters.administracionIds.length > 0) {
    q = q.in('administracion_id', filters.administracionIds);
  } else if (filters.administracionId) {
    q = q.eq('administracion_id', filters.administracionId);
  }
  if (filters.activo === true) q = q.eq('activo', true);
  else if (filters.activo === false) q = q.eq('activo', false);

  if (filters.search && filters.search.trim()) {
    const term = `%${filters.search.trim().toLowerCase()}%`;
    q = q.or(
      `nombre_normalizado.ilike.${term},codigo.ilike.${term},numero_documento.ilike.${term}`,
    );
  }

  q = q.range(from, to);

  const { data, error, count } = await q;
  if (error) {
    log('error', 'edificios.list', error.message, filters);
    return err(error.code ?? 'LIST_FAILED', toUserMessage(error));
  }
  const rows: EdificioRow[] = (data ?? []).map(
    (r: Edificio & { administraciones?: { codigo: string; nombre: string } }) => ({
      ...(r as Edificio),
      administracion_nombre: r.administraciones?.nombre,
      administracion_codigo: r.administraciones?.codigo,
    }),
  );
  return ok({ rows, total: count ?? rows.length, page, pageSize });
}

export async function getEdificio(id: string): Promise<ApiResponse<Edificio | null>> {
  const { data, error } = await supabase
    .from('edificios')
    .select('*')
    .eq('id', id)
    .maybeSingle();
  if (error) {
    log('error', 'edificios.get', error.message, { id });
    return err(error.code ?? 'GET_FAILED', toUserMessage(error));
  }
  return ok(data);
}

/** Tipo aceptado por el cliente. `nombre_normalizado` lo calcula el trigger. */
export type EdificioCreatePayload = Omit<EdificioInsert, 'nombre_normalizado'>;

export async function createEdificio(
  input: EdificioCreatePayload,
): Promise<ApiResponse<Edificio>> {
  // Placeholder para satisfacer el tipo Insert — el trigger
  // normalize_edificio_nombre lo sobreescribe inmediatamente con lower+unaccent.
  const toInsert: EdificioInsert = {
    ...input,
    nombre_normalizado: input.nombre.toLowerCase(),
  };
  const { data, error } = await supabase
    .from('edificios')
    .insert(toInsert)
    .select('*')
    .single();
  if (error) {
    log('error', 'edificios.create', error.message, input);
    // 23505 enriquecido: trae el nombre del edificio existente para que
    // el toast diga "Ya existe el edificio "AVAXON SRL" con código 2..."
    const msg = await enrichConstraintMessage(error, supabase);
    return err(error.code ?? 'CREATE_FAILED', msg);
  }
  return ok(data);
}

export async function updateEdificio(
  id: string,
  patch: EdificioUpdate,
): Promise<ApiResponse<Edificio>> {
  const { data, error } = await supabase
    .from('edificios')
    .update(patch)
    .eq('id', id)
    .select('*')
    .single();
  if (error) {
    log('error', 'edificios.update', error.message, { id });
    const msg = await enrichConstraintMessage(error, supabase);
    return err(error.code ?? 'UPDATE_FAILED', msg);
  }
  return ok(data);
}

export async function toggleEdificioActivo(
  id: string,
  activo: boolean,
): Promise<ApiResponse<Edificio>> {
  return updateEdificio(id, { activo });
}

export async function toggleFacturarConCuitAdministracion(
  id: string,
  value: boolean,
): Promise<ApiResponse<Edificio>> {
  return updateEdificio(id, { facturar_con_cuit_administracion: value });
}

export async function deleteEdificio(id: string): Promise<ApiResponse<null>> {
  const { error } = await supabase.from('edificios').delete().eq('id', id);
  if (error) {
    log('error', 'edificios.delete', error.message, { id });
    return err(error.code ?? 'DELETE_FAILED', toUserMessage(error));
  }
  return ok(null);
}
